import React, { FC, ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { FullScreenLoader } from 'enova-frontend-components';

import { BuildingId } from '../../utils/navigation';
import { useGetBuildingAddress } from '../useGetBuildingAddress';
import { ErrorBox } from '../../components/errorBox';

import BuildingDetailsContext, { BuildingDetailsValues } from './context';
import { useBuildingData, useBuildingEnergyAnalyses } from './buildingDetailsHooks';
import { createSoknadStegMap, getNextSoknadSteg } from './utils';

const BuildingDetailsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { buildingId } = useParams<BuildingId>();
  const { getBuildingAddress } = useGetBuildingAddress();
  const navigate = useNavigate();

  const { buildingError, buildingLoading, building } = useBuildingData(buildingId);

  const { simpleEnergyAnalyses, simpleEnergyAnalysesError, simpleEnergyAnalysesLoading, refreshSimpleEnergyAnalyses } =
    useBuildingEnergyAnalyses(buildingId);

  if (buildingLoading) {
    return <FullScreenLoader open />;
  }

  if (buildingError) {
    return (
      <ErrorBox
        onRetry={() => navigate(0)}
        descriptionKey="building.error"
        error={buildingError}
      />
    );
  }

  if (!building) {
    return <Navigate to="/" />;
  }

  const { adresse, gyldigeEnergiattester } = building;

  const address = getBuildingAddress(adresse);

  const currentEnergyAnalysis = simpleEnergyAnalyses?.find(
    ({ registreringId }) => registreringId === gyldigeEnergiattester?.[0]?.registreringId
  );

  const values: BuildingDetailsValues = {
    address,
    building,
    currentEnergyAnalysis,
    getNextSoknadSteg: () => getNextSoknadSteg(building, simpleEnergyAnalyses),
    refreshEnergyAnalysis: () => refreshSimpleEnergyAnalyses(),
    simpleEnergyAnalyses: simpleEnergyAnalyses,
    simpleEnergyAnalysesError,
    simpleEnergyAnalysesLoading,
    soknadStegMap: createSoknadStegMap(building, simpleEnergyAnalyses)
  };

  return <BuildingDetailsContext.Provider value={values}>{children}</BuildingDetailsContext.Provider>;
};

export default BuildingDetailsProvider;
